import React, { useState, useRef, useEffect, useCallback } from "react";
import ReactMarkdown from "react-markdown"; // Import react-markdown
import { 
  uploadFileApi, 
  sendChatMessageApi, 
  listS3FilesApi, 
  deleteS3FileApi, 
  getS3PresignedUrlApi,
  getFileProcessingStatusApi,
  listScrapedUrlsApi, // Import the new API function
  scrapeUrlApi // Import the new scrape URL API function
} from "../api/bytechat-ws";

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return new Date(dateString).toLocaleString();
  } catch (e) {
    return dateString;
  }
};

const ByteChat = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false); // For File Drawer
  const [isUrlDrawerOpen, setIsUrlDrawerOpen] = useState(false); // For URL Drawer
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");

  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [uploadedFileContext, setUploadedFileContext] = useState(null);
  const [chatError, setChatError] = useState("");

  const [s3FilesList, setS3FilesList] = useState([]);
  const [isLoadingS3Files, setIsLoadingS3Files] = useState(false);
  const [s3FilesError, setS3FilesError] = useState("");
  const [deletingFileKey, setDeletingFileKey] = useState(null);
  const [downloadingFileKey, setDownloadingFileKey] = useState(null);
  const [fileProcessingStatusesMap, setFileProcessingStatusesMap] = useState({});

  const [scrapedUrlsList, setScrapedUrlsList] = useState([]);
  const [isLoadingScrapedUrls, setIsLoadingScrapedUrls] = useState(false);
  const [scrapedUrlsError, setScrapedUrlsError] = useState("");
  const [scrapeUrlInput, setScrapeUrlInput] = useState("");
  const [isScrapingUrl, setIsScrapingUrl] = useState(false);
  const [scrapeMessage, setScrapeMessage] = useState("");
  const [scrapeMessageType, setScrapeMessageType] = useState("info");


  const pollingTimeoutIdRef = useRef(null); // Ref to store polling timeout ID

  const chatMessagesEndRef = useRef(null);

  const scrollToBottom = () => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (chatMessages.length > 0) {
      scrollToBottom(); // Only scroll to bottom when there are messages
    }
  }, [chatMessages]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
      setUploadedFileContext(null);
      setMessage("");
    } else {
      setSelectedFile(null);
      setFileName("");
    }
  };

  const fetchS3Files = useCallback(async () => {
    setIsLoadingS3Files(true);
    setS3FilesError("");
    try {
      const files = await listS3FilesApi();
      setS3FilesList(Array.isArray(files) ? files : []);
    } catch (error) {
      console.error("Error fetching S3 files list:", error);
      setS3FilesError(`Failed to load S3 files: ${error.message}`);
      setS3FilesList([]);
    } finally {
      setIsLoadingS3Files(false);
    }
  }, []);

  useEffect(() => {
    fetchS3Files();
  }, [fetchS3Files]);

  const fetchScrapedUrls = useCallback(async () => {
    setIsLoadingScrapedUrls(true);
    setScrapedUrlsError("");
    try {
      const urls = await listScrapedUrlsApi();
      setScrapedUrlsList(Array.isArray(urls) ? urls : []);
    } catch (error) {
      console.error("Error fetching scraped URLs list:", error);
      setScrapedUrlsError(`Failed to load scraped URLs: ${error.message}`);
      setScrapedUrlsList([]);
    } finally {
      setIsLoadingScrapedUrls(false);
    }
  }, []);

  useEffect(() => {
    fetchScrapedUrls();
  }, [fetchScrapedUrls]);

  const handleScrapeUrl = async () => {
    if (!scrapeUrlInput.trim()) {
      setScrapeMessage("Please enter a URL to scrape.");
      setScrapeMessageType("error");
      return;
    }
    setIsScrapingUrl(true);
    setScrapeMessage("");
    setScrapedUrlsError(""); // Clear previous general errors

    try {
      const response = await scrapeUrlApi(scrapeUrlInput);
      setScrapeMessage(response.message || `Scraping request for "${scrapeUrlInput}" sent successfully.`);
      setScrapeMessageType("success");
      setScrapeUrlInput(""); // Clear input after successful request
      // Optionally, refresh the list of scraped URLs after a short delay or based on response
      setTimeout(() => {
        fetchScrapedUrls();
      }, 2000); // Refresh after 2 seconds
    } catch (error) {
      console.error("Error scraping URL:", error);
      setScrapeMessage(`Failed to scrape URL: ${error.message}`);
      setScrapeMessageType("error");
    } finally {
      setIsScrapingUrl(false);
    }
  };

  // Polling logic for file statuses
  const checkFileStatuses = useCallback(async () => {
    try {
      const response = await getFileProcessingStatusApi(); // May return { pending_files: [...] }
      
      const newStatusesMap = {};
      let hasPendingOrProcessingFiles = false;
      let pendingOrProcessingCount = 0;

      // Handle response structure: { pending_files: [{id: "filename.pdf", status: "processing"}, ...] }
      const statusesArray = response?.pending_files || response;

      if (Array.isArray(statusesArray)) {
        statusesArray.forEach(statusObj => {
          if (statusObj && statusObj.id) { // 'id' is the filename
            newStatusesMap[statusObj.id] = statusObj; // Store the full status object
            if (statusObj.status === 'pending' || statusObj.status === 'processing') {
              hasPendingOrProcessingFiles = true;
              pendingOrProcessingCount++;
            }
          }
        });
      }
      
      setFileProcessingStatusesMap(newStatusesMap);

      if (hasPendingOrProcessingFiles) {
        console.log(`${pendingOrProcessingCount} file(s) are still pending/processing. Polling again in 5 minutes.`);
        if (pollingTimeoutIdRef.current) {
          clearTimeout(pollingTimeoutIdRef.current);
        }
        pollingTimeoutIdRef.current = setTimeout(checkFileStatuses, 60000); 
      } else {
        console.log("All files processed or no pending files found. Stopping polling.");
        if (pollingTimeoutIdRef.current) {
          clearTimeout(pollingTimeoutIdRef.current);
          pollingTimeoutIdRef.current = null;
        }
        fetchS3Files(); 
      }
    } catch (error) {
      console.error("Error polling file statuses:", error);
      if (pollingTimeoutIdRef.current) {
        clearTimeout(pollingTimeoutIdRef.current);
        pollingTimeoutIdRef.current = null;
      }
    }
  }, [fetchS3Files]);

  useEffect(() => {
    // Start polling for file statuses on component mount
    checkFileStatuses();

    // Cleanup function to clear timeout when component unmounts
    return () => {
      if (pollingTimeoutIdRef.current) {
        clearTimeout(pollingTimeoutIdRef.current);
      }
    };
  }, [checkFileStatuses]);

  const handleUpload = async () => {
    if (selectedFile) {
      setIsLoading(true);
      setMessage("");
      setChatError("");
      try {
        console.log("Uploading file:", selectedFile);
        const response = await uploadFileApi(selectedFile);
        console.log("Upload request sent (no-cors):", response);

        setUploadedFileContext(selectedFile.name);
        setMessage(
          `File "${selectedFile.name}" upload request sent. Chat about it below or start a new conversation.`
        );
        setMessageType("success");
        // Refresh S3 files list after an upload attempt
        fetchS3Files();
        
        // Clear any existing polling timeout before starting a new delayed polling sequence.
        // This ensures that if polling was already active, it's reset.
        if (pollingTimeoutIdRef.current) {
            clearTimeout(pollingTimeoutIdRef.current);
            pollingTimeoutIdRef.current = null; 
        }

        // Start polling for statuses after a 10-second delay
        setTimeout(() => {
            checkFileStatuses();
        }, 10000); // 10 seconds delay

      } catch (error) {
        console.error("Upload error (network or fetch issue):", error);
        setMessage(`Error sending upload request: ${error.message}`);
        setMessageType("error");
      } finally {
        setIsLoading(false);
      }
    } else {
      setMessage("Please select a file first.");
      setMessageType("error");
    }
  };

  const handleDeleteFile = async (fileKey) => {
    setDeletingFileKey(fileKey);
    setS3FilesError("");
    setMessage("");
    try {
      const response = await deleteS3FileApi(fileKey);
      console.log(`Delete response for ${fileKey}:`, response);
      setMessage(response.message || `File "${fileKey}" deleted successfully.`);
      setMessageType("success");
      fetchS3Files();
    } catch (error) {
      console.error(`Error deleting file ${fileKey}:`, error);
      setS3FilesError(`Failed to delete "${fileKey}": ${error.message}`);
    } finally {
      setDeletingFileKey(null);
    }
  };

  const handleDownloadFile = async (fileKey) => {
    setDownloadingFileKey(fileKey);
    setS3FilesError("");
    setMessage("");
    try {
      const response = await getS3PresignedUrlApi(fileKey);
      if (response && response.presigned_url) {
        // Fetch the file content using the presigned URL
        const fileResponse = await fetch(response.presigned_url);
        if (!fileResponse.ok) {
          throw new Error(`Failed to fetch file: ${fileResponse.status} ${fileResponse.statusText}`);
        }
        const blob = await fileResponse.blob();
        
        // Create an object URL for the blob
        const objectUrl = URL.createObjectURL(blob);
        
        const link = document.createElement("a");
        link.href = objectUrl;
        link.setAttribute("download", fileKey); // Set the desired filename
        document.body.appendChild(link);
        link.click();
        
        // Clean up by revoking the object URL and removing the link
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
        
        setMessage(`Download for "${fileKey}" initiated.`);
        setMessageType("success");
      } else {
        throw new Error("Presigned URL not found in response.");
      }
    } catch (error) {
      console.error(`Error downloading file ${fileKey}:`, error);
      setS3FilesError(`Failed to download "${fileKey}": ${error.message}`);
    } finally {
      setDownloadingFileKey(null);
    }
  };

  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;

    const userMessageText = chatInput; // This is the new prompt
    const userMessageForUI = { sender: "user", text: userMessageText };
    const aiMessageId = `ai-${Date.now()}`;
    
    // Prepare history for the API
    // Map existing chatMessages (before adding the current one) to the { role, content } format
    const historyForApi = chatMessages.map(msg => ({
      role: msg.sender === "user" ? "user" : "assistant",
      content: msg.text
    }));

    // Update UI with the new user message and a placeholder for AI response
    setChatMessages((prevMessages) => [
      ...prevMessages,
      userMessageForUI,
      { sender: "ai", text: "", id: aiMessageId },
    ]);

    setChatInput("");
    setIsSendingMessage(true);
    setChatError("");

    try {
      const payload = {
        prompt: userMessageText, // Current user input
        model: "qwen2.5vl:7b-q8_0", // Or your desired model
        history: historyForApi, // Previous conversation messages
        stream: true,
      };

      console.log("Sending to AI (streaming):", JSON.stringify(payload, null, 2));

      let accumulatedAiText = "";
      for await (const chunk of sendChatMessageApi(payload)) {
        if (chunk.rawText) {
          accumulatedAiText += chunk.rawText;

          // Update the AI message in the chat UI in real-time
          setChatMessages((prevMessages) =>
            prevMessages.map((msg) =>
              msg.id === aiMessageId ? { ...msg, text: accumulatedAiText } : msg
            )
          );
        }
      }
    } catch (error) {
      console.error("Error sending chat message or processing stream:", error);
      setChatError(`AI Error: ${error.message}`);
      setChatMessages((prevMessages) =>
        prevMessages.map((msg) =>
          msg.id === aiMessageId
            ? { ...msg, text: `Sorry, I couldn't get a response. ${error.message}` }
            : msg
        )
      );
    } finally {
      setIsSendingMessage(false);
    }
  };

  const getAlertClasses = (type) => {
    switch (type) {
      case "success":
        return "bg-green-100 border-green-400 text-green-700";
      case "error":
        return "bg-red-100 border-red-400 text-red-700";
      default:
        return "bg-blue-100 border-blue-400 text-blue-700";
    }
  };

  const toggleDrawer = () => { // For File Drawer
    setIsDrawerOpen(!isDrawerOpen);
    if (isUrlDrawerOpen && !isDrawerOpen) setIsUrlDrawerOpen(false); // Close other drawer if opening this one
  };

  const toggleUrlDrawer = () => { // For URL Drawer
    setIsUrlDrawerOpen(!isUrlDrawerOpen);
    if (isDrawerOpen && !isUrlDrawerOpen) setIsDrawerOpen(false); // Close other drawer if opening this one
  };

  // Inline SVG Icons (Heroicons)
  const RefreshIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
    </svg>
  );

  const FileIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 0 0-3.375-3.375h-1.5A1.125 1.125 0 0 1 13.5 7.125v-1.5a3.375 3.375 0 0 0-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 0 0-9-9Z" />
    </svg>
  );

  const UploadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
    </svg>
  );
  
  const SendIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5" />
    </svg>
  );

  const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="m14.74 9-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 0 1-2.244 2.077H8.084a2.25 2.25 0 0 1-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 0 0-3.478-.397m-12.56 0c.342.052.682.107 1.022.166m0 0a48.11 48.11 0 0 1 3.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 0 0-3.32 0c-1.18.037-2.09.922-2.09 2.201v.916m7.5 0a48.667 48.667 0 0 0-7.5 0" />
    </svg>
  );

  const DownloadIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
    </svg>
  );

  const SpinnerIcon = () => (
    <svg className="w-5 h-5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  const LinkIcon = () => ( // New Icon for URLs
    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  );

  return (
    <div className="flex h-screen"> {/* Adjusted for multi-drawer layout */}
      {/* URL Drawer (Left Side) */}
      <div
        className={`fixed top-0 left-0 bg-white shadow-lg transform ${
          isUrlDrawerOpen ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 lg:relative lg:translate-x-0 lg:block`}
        style={{
          width: "300px",
          height: "100vh", // Full height
          zIndex: 1301, // Ensure above MUI modals
          // paddingTop: "64px", // Adjust if you have a global navbar
        }}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-xl font-semibold">Scraped URLs</h3>
          <button
            onClick={toggleUrlDrawer}
            className="text-gray-500 hover:text-gray-700 lg:hidden" // Hide on large screens
            title="Close URLs"
          >
            ✕
          </button>
        </div>
        <div className="flex flex-col flex-1 p-4 overflow-y-auto" style={{maxHeight: 'calc(100vh - 65px)'}}> {/* Adjust 65px based on header height */}
          {/* Add URL input and scrape button here if needed in future */}
          <div className="mb-4">
            <input
              type="url"
              value={scrapeUrlInput}
              onChange={(e) => {
                setScrapeUrlInput(e.target.value);
                setScrapeMessage(""); // Clear message when user types
              }}
              placeholder="Enter URL to scrape"
              className="block w-full p-2 mb-2 text-sm border border-gray-300 rounded-md focus:ring-purple-500 focus:border-purple-500"
              disabled={isScrapingUrl}
            />
            <button
              onClick={handleScrapeUrl}
              disabled={!scrapeUrlInput.trim() || isScrapingUrl}
              className="w-full px-4 py-2 text-white bg-purple-500 rounded hover:bg-purple-600 disabled:bg-gray-300"
            >
              {isScrapingUrl ? "Scraping..." : "Scrape URL"}
            </button>
            {scrapeMessage && (
              <p
                className={`mt-2 text-sm ${
                  scrapeMessageType === "success" ? "text-green-600" : "text-red-600"
                }`}
              >
                {scrapeMessage}
              </p>
            )}
          </div>

          {isLoadingScrapedUrls && <div className="py-4 text-center">Loading URLs...</div>}
          {scrapedUrlsError && (
            <div
              className={`border px-4 py-3 rounded relative mb-2 ${getAlertClasses("error")}`}
              role="alert"
            >
              {scrapedUrlsError}
            </div>
          )}
          {!isLoadingScrapedUrls && !scrapedUrlsError && (
            scrapedUrlsList.length > 0 ? (
              <ul className="flex flex-col space-y-2">
                {scrapedUrlsList.map((item) => ( // Assuming item has id, url, title
                  <li
                    key={item.id || item.url}
                    className="p-2 border rounded-md bg-gray-50"
                  >
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-sm text-blue-600 truncate hover:underline"
                      title={item.url}
                    >
                      {item.title || item.url}
                    </a>
                    {/* Add more details or actions for scraped URLs if needed */}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-4 mb-6 text-sm text-center text-gray-500">
                No scraped URLs found.
              </p>
            )
          )}
        </div>
      </div>

      {/* Chat Window Section (Center) */}
      <div className="flex flex-col flex-1 p-4 mx-auto bg-white rounded-lg shadow-lg lg:my-4 lg:mx-0" style={{ height: "calc(100vh - 2rem)", maxHeight: "800px", maxWidth: "800px" }}> {/* Adjusted for centering and height */}
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={toggleUrlDrawer}
            className="px-3 py-2 text-white bg-purple-500 rounded hover:bg-purple-600 lg:hidden" // Button for URL drawer on small screens
          >
            <LinkIcon />
          </button>
          <h2 className="mx-auto text-xl font-semibold">Chat with AI</h2> {/* Centered title */}
          <button
            onClick={toggleDrawer}
            className="px-3 py-2 text-white bg-blue-500 rounded hover:bg-blue-600 lg:hidden" // Button for File drawer
          >
            <FileIcon />
          </button>
        </div>
        {uploadedFileContext && (
          <p className="mb-2 text-xs text-center text-gray-500">
            Context: Last upload attempt was for "{uploadedFileContext}"
          </p>
        )}
        <div
          className="flex-1 p-3 mb-3 overflow-y-auto border border-gray-300 rounded-md bg-gray-50"
          style={{ maxHeight: "calc(100% - 100px)" }} // Adjust height to make only the inside scrollable
        >
          {chatMessages.map((msg, index) => (
            <div
              key={msg.id || index}
              className={`mb-2 flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`p-2 rounded-lg max-w-xs lg:max-w-md shadow ${
                  msg.sender === "user"
                    ? "bg-blue-500 text-white"
                    : "bg-gray-200 text-gray-800"
                }`}
                style={{
                  borderRadius:
                    msg.sender === "user" ? "10px 10px 0 10px" : "10px 10px 10px 0",
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap", // Preserve spaces and line breaks
                }}
              >
                {msg.sender === "ai" ? (
                  <ReactMarkdown
                    components={{
                      p: ({ node, ...props }) => <p style={{ whiteSpace: "pre-wrap" }} {...props} />, // Ensure pre-wrap for Markdown paragraphs
                    }}
                  >
                    {msg.text}
                  </ReactMarkdown>
                ) : (
                  msg.text // Render plain text for user messages
                )}
              </div>
            </div>
          ))}
          <div ref={chatMessagesEndRef} />
        </div>
        {chatError && (
          <div
            className={`border px-4 py-3 rounded relative mb-2 ${getAlertClasses("error")}`}
            role="alert"
          >
            {chatError}
          </div>
        )}
        <div className="flex items-center">
          <input
            type="text"
            className="flex-grow p-2 border border-gray-300 rounded-l-md focus:ring-blue-500 focus:border-blue-500"
            placeholder="Type your message..."
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            onKeyPress={(e) =>
              e.key === "Enter" && !isSendingMessage && handleSendMessage()
            }
            disabled={isSendingMessage}
          />
          <button
            onClick={handleSendMessage}
            disabled={isSendingMessage || !chatInput.trim()}
            className="flex items-center px-4 py-2 text-white bg-blue-500 rounded-r-md hover:bg-blue-600 disabled:bg-gray-300"
          >
            {isSendingMessage ? (
              <span className="w-5 h-5 border-b-2 border-white rounded-full animate-spin"></span>
            ) : (
              <SendIcon />
            )}
            <span className="ml-1">Send</span>
          </button>
        </div>
      </div>

      {/* Side Drawer for Uploaded Files (Right Side) */}
      <div
        className={`fixed top-0 right-0 bg-white shadow-lg transform ${
          isDrawerOpen ? "translate-x-0" : "translate-x-full"
        } transition-transform duration-300 lg:relative lg:translate-x-0 lg:block`}
        style={{
          width: "300px",
          height: "100vh", // Full height
          zIndex: 1301, // Ensure above MUI modals
          // paddingTop: "64px", // Adjust if you have a global navbar
        }}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-xl font-semibold">Uploaded Files</h3>
          <button
            onClick={toggleDrawer}
            className="text-gray-500 hover:text-gray-700 lg:hidden" // Hide on large screens
            title="Close Files"
          >
            ✕
          </button>
        </div>
        <div className="flex flex-col flex-1 p-4 overflow-y-auto" style={{maxHeight: 'calc(100vh - 65px)'}}> {/* Adjust 65px based on header height */}
          {/* File Upload Section */}
          <div className="mb-4">
            <input
              type="file"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border file:border-gray-300 file:text-sm file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100"
            />
            <button
              onClick={handleUpload}
              disabled={!selectedFile || isLoading}
              className="w-full px-4 py-2 mt-2 text-white bg-blue-500 rounded hover:bg-blue-600 disabled:bg-gray-300"
            >
              {isLoading ? "Uploading..." : "Upload File"}
            </button>
            {message && (
              <p
                className={`mt-2 text-sm ${
                  messageType === "success" ? "text-green-600" : "text-red-600"
                }`}
              >
                {message}
              </p>
            )}
          </div>

          {/* File List Section */}
          {isLoadingS3Files && <div className="py-4 text-center">Loading files...</div>}
          {s3FilesError && (
            <div
              className={`border px-4 py-3 rounded relative mb-2 ${getAlertClasses("error")}`}
              role="alert"
            >
              {s3FilesError}
            </div>
          )}
          {!isLoadingS3Files && !s3FilesError && (
            s3FilesList.length > 0 ? (
              <ul className="flex flex-col space-y-2">
                {s3FilesList.map((file, index) => {
                  const currentFileStatus = fileProcessingStatusesMap[file.key];
                  let statusBorderClass = "border-gray-200"; // Default border
                  let statusTextClass = "text-gray-700"; // Default text
                  let statusBackgroundClass = "bg-gray-50"; // Default background
                  let showLoadingIndicator = false;

                  if (currentFileStatus) {
                    if (currentFileStatus.status === 'pending' || currentFileStatus.status === 'processing') {
                      statusBorderClass = "border-yellow-500";
                      statusTextClass = "text-yellow-800";
                      statusBackgroundClass = "bg-yellow-50";
                      showLoadingIndicator = true;
                    } else if (currentFileStatus.status === 'failed') {
                      statusBorderClass = "border-red-500";
                      statusTextClass = "text-red-800";
                      statusBackgroundClass = "bg-red-50";
                    }
                    // Add more conditions for other statuses like 'completed' if needed
                  }

                  return (
                    <li
                      key={file.key || index}
                      className={`flex items-center justify-between p-2 border rounded-md ${statusBorderClass} ${statusBackgroundClass}`}
                    >
                      <div className="flex items-center space-x-2">
                        {showLoadingIndicator ? (
                          <div className={`flex items-center justify-center w-12 h-5 ${statusTextClass}`}> {/* Adjust width to align with buttons */}
                            <SpinnerIcon />
                          </div>
                        ) : (
                          <>
                            <button
                              onClick={() => handleDownloadFile(file.key)}
                              disabled={downloadingFileKey === file.key}
                              className="text-blue-500 hover:text-blue-700 disabled:text-gray-400"
                              title="Download"
                            >
                              <DownloadIcon />
                            </button>
                            <button
                              onClick={() => handleDeleteFile(file.key)}
                              disabled={deletingFileKey === file.key}
                              className="text-red-500 hover:text-red-700 disabled:text-gray-400"
                              title="Delete"
                            >
                              <TrashIcon />
                            </button>
                          </>
                        )}
                      </div>
                      <span className={`text-sm truncate ${statusTextClass}`} title={file.key}>
                        {file.key}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="mt-4 mb-6 text-sm text-center text-gray-500">
                No files found or list is empty.
              </p>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default ByteChat;
